import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { totpSetup, ApiError } from '../lib/api'
import { AuthLayout, ErrorBox, Field, SubmitButton } from '../components/AuthLayout'

// Step 2: the secret was already generated and stored server-side (SetPendingTOTPSecret);
// this only ever displays it once, for scanning or manual entry, and only ever sends the
// 6-digit confirmation code back — never the secret itself (that used to round-trip
// through a hidden form field in the SSR version; see the Critical finding it replaced).
export function TotpSetupConfirm() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [secret, setSecret] = useState('')
  const [otpAuthUrl, setOtpAuthUrl] = useState('')
  const [loadError, setLoadError] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    totpSetup
      .pending()
      .then((res) => {
        setSecret(res.secret)
        setOtpAuthUrl(res.otpauth_url)
      })
      .catch((err) => {
        setLoadError(err instanceof ApiError ? err.message : 'Deze inschrijfpoging is verlopen of al voltooid.')
      })
  }, [])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      await totpSetup.confirm(code)
      await queryClient.invalidateQueries({ queryKey: ['me'] })
      // Enrolling revokes every OTHER session server-side, but the current one is
      // reissued with totp_ok=true in the same response, so this lands straight on the
      // dashboard rather than forcing a fresh login.
      navigate('/dashboard')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Code klopt niet.')
    } finally {
      setBusy(false)
    }
  }

  if (loadError) {
    return (
      <AuthLayout title="Tweestapsverificatie instellen">
        <ErrorBox>{loadError}</ErrorBox>
        <SubmitButton onClick={() => navigate('/totp/setup')}>Opnieuw beginnen</SubmitButton>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Tweestapsverificatie instellen">
      <ErrorBox>{error}</ErrorBox>
      {secret && (
        <>
          <p className="mb-3 text-sm text-ink-soft">Scan met je authenticator-app, of voer de sleutel handmatig in:</p>
          {otpAuthUrl && (
            <div className="mb-3 inline-block rounded-lg bg-white p-3">
              <QRCodeSVG value={otpAuthUrl} size={168} />
            </div>
          )}
          <div className="mb-4 rounded-lg border border-line bg-card p-2.5 font-mono text-sm break-all">{secret}</div>
        </>
      )}
      <form onSubmit={onSubmit}>
        <Field
          label="Code uit je app"
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <SubmitButton disabled={busy || !secret}>Inschakelen</SubmitButton>
      </form>
    </AuthLayout>
  )
}
